/** Cash Buyers page (v1 buyers-agent.js buyersView). A filterable table of your
 * dispo buyers with an inline add/edit form. Writes the whole BUYERS array
 * through the settings door (gated by liveFire `settingsEdit`). */
import { useMemo, useRef, useState } from "react";
import {
  allBuyers, filterBuyers, priceRange, statusHue, buyerId, buyerStates, buyerTags,
  buyersToCsv, csvToBuyers, narrowBuyer,
  BUYER_TYPES, BUYER_STATUS, BUYER_FINANCING, BUYER_EXIT, type Buyer,
} from "./api";
import { useConfirmDelete } from "../common/ConfirmDelete";

export function BuyersPage({ orgSettings, onSave, canEdit }: {
  orgSettings: Record<string, unknown>;
  onSave: (all: Buyer[]) => void;
  canEdit: boolean;
}) {
  const buyers = useMemo(() => allBuyers(orgSettings), [orgSettings]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [state, setState] = useState("all");
  const [tag, setTag] = useState("all");
  const [editing, setEditing] = useState<Buyer | null>(null);
  const confirmDel = useConfirmDelete(); // v1 deleteBuyer — typed-delete gated
  const [importNote, setImportNote] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const states = useMemo(() => buyerStates(buyers), [buyers]);
  const tags = useMemo(() => buyerTags(buyers), [buyers]);
  const rows = useMemo(() => filterBuyers(buyers, { q, type, status, state, tag }), [buyers, q, type, status, state, tag]);

  const exportCsv = () => {
    const csv = buyersToCsv(rows);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "buyers.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const importCsv = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = csvToBuyers(String(reader.result ?? ""));
      if (!parsed.length) { setImportNote("No buyers found in that file."); return; }
      // de-dupe against existing by name+phone (v1 had no import; we merge safely)
      const seen = new Set(buyers.map((b) => (b.n + "|" + b.ph).toLowerCase()));
      const fresh = parsed.filter((b) => !seen.has((b.n + "|" + b.ph).toLowerCase()));
      onSave([...fresh.map((b) => narrowBuyer({ ...b, id: buyerId() })), ...buyers]);
      setImportNote(`Imported ${fresh.length} buyer${fresh.length === 1 ? "" : "s"}${parsed.length - fresh.length ? ` (${parsed.length - fresh.length} duplicate${parsed.length - fresh.length === 1 ? "" : "s"} skipped)` : ""}.`);
    };
    reader.readAsText(file);
  };

  const startNew = () => setEditing({ id: "", n: "", co: "", type: "Cash Buyer", status: "Active", area: "", ph: "", em: "", min: 0, max: 0, state: "", tags: [], ph2: "", em2: "", counties: "", cities: "", financing: "", exit: "", notes: "" });
  const commit = (b: Buyer) => {
    const withId: Buyer = b.id ? b : { ...b, id: buyerId() };
    onSave(b.id ? buyers.map((x) => (x.id === b.id ? withId : x)) : [withId, ...buyers]);
    setEditing(null);
  };
  const remove = (id: string) => onSave(buyers.filter((x) => x.id !== id));

  return (
    <div className="leads exppage">
      <div className="exphead">
        <h1>Cash Buyers</h1>
        <div className="expacts">
          <span className="expcat">{buyers.length} buyer{buyers.length === 1 ? "" : "s"}</span>
          <button className="btn slim" onClick={exportCsv} disabled={rows.length === 0} title="Download the filtered list as CSV">↓ Export CSV</button>
          {canEdit ? <button className="btn slim" onClick={() => fileRef.current?.click()} title="Import buyers from a CSV file">↑ Import CSV</button> : null}
          {canEdit ? <button className="btn slim primary" onClick={startNew}>+ Add buyer</button> : null}
          <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) importCsv(f); e.target.value = ""; }} />
        </div>
      </div>
      <div className="expqbo">Your dispo rolodex — who to send deals to. The SMS blast that uses this list turns on with the Quo connection.{canEdit ? "" : " Editing is view-only on this device."}</div>

      <div className="bar bybar">
        <input placeholder="Search name, company, area, phone…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="all">All types</option>
          {BUYER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">All statuses</option>
          {BUYER_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {states.length ? (
          <select value={state} onChange={(e) => setState(e.target.value)} title="Filter by state">
            <option value="all">All states</option>
            {states.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        ) : null}
        {tags.length ? (
          <select value={tag} onChange={(e) => setTag(e.target.value)} title="Filter by tag">
            <option value="all">All tags</option>
            {tags.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        ) : null}
      </div>
      {importNote ? <div className="expqbo">{importNote}</div> : null}

      {editing ? <BuyerForm initial={editing} onCancel={() => setEditing(null)} onSave={commit} /> : null}

      {rows.length === 0 ? (
        <div className="expempty">{buyers.length === 0 ? (canEdit ? "No buyers yet — click Add buyer." : "No buyers yet.") : "No buyers match."}</div>
      ) : (
        <div className="bycards">
          {rows.map((b) => {
            const verified = b.status === "Active" || b.status === "VIP";
            return (
              <div key={b.id} className="bycard">
                <div className="bycardhead">
                  <div className="cavatar">{byInitials(b.n)}</div>
                  <div className="cwho">
                    <strong>
                      <span className="bynametext">{b.n || "—"}</span>
                      {verified ? <span className="byverif" title={b.status}>✓</span> : null}
                    </strong>
                    {b.co ? <div className="pdsub">{b.co}</div> : null}
                  </div>
                  <span className={"bystatus hue-" + statusHue(b.status)}>{b.status}</span>
                </div>
                <div className="bypills">
                  <span className="expcat">{b.type}</span>
                  {b.state ? <span className="expcat">{b.state}</span> : null}
                </div>
                <div className="bylines">
                  <div className="byfield"><span className="byk">Buy-box</span><span className="byv" title={b.area || undefined}>{b.area || "—"}</span></div>
                  <div className="byfield"><span className="byk">Budget</span><span className="byv">{priceRange(b)}</span></div>
                  {b.ph ? <a className="cline" href={`tel:${b.ph}`}>☎ {b.ph}</a> : null}
                  {b.em ? <a className="cline" href={`mailto:${b.em}`}>✉ {b.em}</a> : null}
                  {!b.ph && !b.em ? <span className="cline">No contact info</span> : null}
                </div>
                {b.tags.length ? <div className="bytags">{b.tags.map((t) => <span key={t} className="bytag">{t}</span>)}</div> : null}
                {canEdit ? (
                  <div className="byfoot">
                    <button className="rowbtn" title="Edit" onClick={() => setEditing(b)}>✎ Edit</button>
                    {/* v1 deleteBuyer — typed-delete gated, exact wording */}
                    <button className="rowbtn del" title="Delete" onClick={() => confirmDel.ask(`Delete buyer "${b.n}"?`, () => remove(b.id))}>✕</button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
      {confirmDel.modal}
    </div>
  );
}

const numOr0 = (s: string): number => { const n = Number(s.replace(/[^0-9.]/g, "")); return Number.isFinite(n) ? n : 0; };

/** Two-letter avatar initials from a buyer name (presentation only). */
const byInitials = (n: string): string =>
  n.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join("") || "?";

function BuyerForm({ initial, onCancel, onSave }: { initial: Buyer; onCancel: () => void; onSave: (b: Buyer) => void }) {
  const [b, setB] = useState<Buyer>(initial);
  const set = (patch: Partial<Buyer>) => setB((prev) => ({ ...prev, ...patch }));
  const ok = b.n.trim().length > 0;
  return (
    <div className="expform">
      <div className="expformtitle">{initial.id ? "Edit buyer" : "Add buyer"}</div>
      <div className="expformsec">Contact</div>
      <div className="expformgrid">
        <label className="wide"><span>Name</span><input value={b.n} placeholder="e.g. John Smith" onChange={(e) => set({ n: e.target.value })} /></label>
        <label><span>Company</span><input value={b.co} onChange={(e) => set({ co: e.target.value })} /></label>
        <label><span>Type</span><select value={b.type} onChange={(e) => set({ type: e.target.value })}>{BUYER_TYPES.map((t) => <option key={t}>{t}</option>)}</select></label>
        <label><span>Status</span><select value={b.status} onChange={(e) => set({ status: e.target.value })}>{BUYER_STATUS.map((s) => <option key={s}>{s}</option>)}</select></label>
        <label><span>Phone</span><input value={b.ph} onChange={(e) => set({ ph: e.target.value })} /></label>
        <label><span>Secondary Phone</span><input value={b.ph2} onChange={(e) => set({ ph2: e.target.value })} /></label>
        <label><span>Email</span><input value={b.em} onChange={(e) => set({ em: e.target.value })} /></label>
        <label><span>Secondary Email</span><input value={b.em2} onChange={(e) => set({ em2: e.target.value })} /></label>
      </div>
      <div className="expformsec">Buy-box &amp; Preferences</div>
      <div className="expformgrid">
        <label className="wide"><span>Buy-box area</span><input value={b.area} placeholder="e.g. Orange & Volusia counties" onChange={(e) => set({ area: e.target.value })} /></label>
        <label><span>State</span><input value={b.state} placeholder="FL" onChange={(e) => set({ state: e.target.value })} /></label>
        <label><span>Target Counties</span><input value={b.counties} placeholder="e.g. Orange, Volusia" onChange={(e) => set({ counties: e.target.value })} /></label>
        <label><span>Target Cities</span><input value={b.cities} placeholder="e.g. Orlando, Deltona" onChange={(e) => set({ cities: e.target.value })} /></label>
        <label><span>Price min ($)</span><input type="number" value={b.min || ""} onChange={(e) => set({ min: numOr0(e.target.value) })} /></label>
        <label><span>Price max ($)</span><input type="number" value={b.max || ""} onChange={(e) => set({ max: numOr0(e.target.value) })} /></label>
        <label><span>Financing</span><select value={b.financing} onChange={(e) => set({ financing: e.target.value })}><option value="">— Select —</option>{BUYER_FINANCING.map((f) => <option key={f}>{f}</option>)}</select></label>
        <label><span>Exit strategy</span><select value={b.exit} onChange={(e) => set({ exit: e.target.value })}><option value="">— Select —</option>{BUYER_EXIT.map((x) => <option key={x}>{x}</option>)}</select></label>
        <label className="wide"><span>Tags</span><input value={b.tags.join(", ")} placeholder="e.g. Great buyer, Fast close" onChange={(e) => set({ tags: e.target.value.split(/[;,]/).map((t) => t.trim()).filter(Boolean) })} /></label>
        <label className="full"><span>Notes</span><textarea value={b.notes} placeholder="Any additional notes about this buyer" onChange={(e) => set({ notes: e.target.value })} /></label>
      </div>
      <div className="expformacts">
        <button className="btn slim primary" disabled={!ok} onClick={() => onSave({ ...b, n: b.n.trim() })}>{initial.id ? "Save changes" : "Add buyer"}</button>
        <button className="btn slim" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
